import React, { useCallback, useMemo, useState } from 'react';
import { Box, Flex, Text, Button, Badge, Image, Input, Icon, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { type ColumnDef, type RowSelectionState, type OnChangeFn } from '@tanstack/react-table';
import DataTable, { type SortState } from '../table/DataTable';
import { FilterSidebar, type FilterSection } from '../FilterSidebar';
import { FilterIcon } from '../DataTable';
import { FloatingAudioPlayer } from './FloatingAudioPlayer';
import { LibraryFilterBar } from './LibraryFilterBar';
import { TagMultiSelect } from '../forms/TagMultiSelect';
import { useLibraryFilters } from '../../hooks/useLibraryFilters';
import { useLibraryTags } from '../../features/library/hooks/useLibraryTags';
import {
  fetchLibraryTracks,
  getStreamUrl,
  type LibraryTrackDto,
} from '../../api/libraryApi';
import { TrackStatusBadge } from '../../features/library/components/TrackStatusBadge';

export interface LibraryBrowserPanelProps {
  /** 'standalone' = full library page; 'selector' = side panel with checkboxes */
  mode: 'standalone' | 'selector';
  /** Callback when tracks are selected (selector mode only) */
  onTracksSelected?: (trackIds: string[]) => void;
  /** Track IDs to exclude from the list (already added to playlist) */
  excludeTrackIds?: string[];
  /** Externally controlled selected track IDs */
  selectedTrackIds?: string[];
  /** Callback when a track is clicked (standalone mode — navigate to detail) */
  onTrackClick?: (trackId: string) => void;
  /** Callback when reprocess is requested for a track */
  onReprocess?: (trackId: string) => void;
  /** Optional action button rendered inside the card header (e.g. Upload) */
  actionButton?: React.ReactNode;
  /**
   * Standalone mode: enables row-selection checkboxes + an "Edit selected (N)"
   * toolbar button. Fires with the currently-selected track ids. When omitted,
   * standalone mode has no selection UI (existing behavior).
   */
  onEditSelected?: (trackIds: string[]) => void;
  /**
   * Standalone-mode bulk action: manage playlists for the selected tracks. Fires with the
   * currently-selected track ids. Enables the row-selection UI just like onEditSelected.
   */
  onManagePlaylists?: (trackIds: string[]) => void;
  /**
   * Standalone-mode bulk action: hard-delete the selected library tracks. Fires with
   * the currently-selected track ids. Enables the row-selection UI like onEditSelected.
   */
  onDeleteSelected?: (trackIds: string[]) => void;
}

type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

const DEFAULT_PAGE_SIZE = 20;

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function LibraryBrowserPanel({
  mode,
  onTracksSelected,
  excludeTrackIds = [],
  selectedTrackIds: controlledSelectedIds,
  onTrackClick,
  onReprocess,
  actionButton,
  onEditSelected,
  onManagePlaylists,
  onDeleteSelected,
}: LibraryBrowserPanelProps) {
  const { t } = useTranslation();
  const {
    filters,
    searchInput,
    setSearchInput,
    toggleGenre,
    toggleMood,
    toggleStatus,
    setGenres,
    setMoods,
    setStatuses,
    setTags,
  } = useLibraryFilters();

  // Tag suggestion pool (FR-048) — shared across this component, the playlist
  // track-picker side-panel, and TrackDetailPage via the same react-query key.
  const { data: tagPool = [] } = useLibraryTags();

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const placeholderBg = useColorModeValue('gray.200', 'whiteAlpha.200');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<SortState | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(mode === 'standalone');

  // ── Selection state (selector mode) ──
  // Source of truth is a Set keyed by track id, NOT row index, so a selection
  // survives page changes and filter changes (the underlying `data` array is
  // replaced on every page/filter change, but ids are stable). DataTable is fed
  // a derived RowSelectionState (also keyed by id via getRowId) and reports
  // changes back through onRowSelectionChange.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  // ── Shared floating player state (standalone mode) ──
  const [playingTrack, setPlayingTrack] = useState<LibraryTrackDto | null>(null);
  const [playerStreamUrl, setPlayerStreamUrl] = useState<string | null>(null);
  const [playerSasToken, setPlayerSasToken] = useState<string | null>(null);

  const handlePlayToggle = useCallback(async (track: LibraryTrackDto) => {
    if (playingTrack?.id === track.id) {
      setPlayingTrack(null);
      setPlayerStreamUrl(null);
      setPlayerSasToken(null);
      return;
    }
    try {
      const result = await getStreamUrl(track.id);
      setPlayingTrack(track);
      setPlayerStreamUrl(result.streamUrl);
      setPlayerSasToken(result.sasToken);
    } catch {
      setPlayingTrack(null);
    }
  }, [playingTrack?.id]);

  const handleClosePlayer = useCallback(() => {
    setPlayingTrack(null);
    setPlayerStreamUrl(null);
    setPlayerSasToken(null);
  }, []);

  // Paginated query (replaces useInfiniteLibrary)
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'admin-library',
      filters.search,
      filters.genres,
      filters.moods,
      filters.statuses,
      filters.tags,
      page,
      pageSize,
      sort?.id,
      sort?.desc,
    ],
    queryFn: () =>
      fetchLibraryTracks({
        page: page + 1, // API is 1-based, DataTable page is 0-based
        pageSize,
        search: filters.search || undefined,
        genre: filters.genres.length > 0 ? filters.genres.join(',') : undefined,
        mood: filters.moods.length > 0 ? filters.moods.join(',') : undefined,
        status: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
        // Tag filter (FR-060) — comma-separated; AND semantics applied server-side.
        tags: filters.tags.length > 0 ? filters.tags.join(',') : undefined,
        sortBy: sort?.id || undefined,
        sortDesc: sort ? sort.desc : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const allTracks = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;

  // Already-in-playlist tracks: rendered as DISABLED "Already added" rows in
  // selector mode (instead of being hidden), so curators can see what they
  // already have. They are never selectable / addable.
  const excludeSet = useMemo(() => new Set(excludeTrackIds), [excludeTrackIds]);
  const tracks = allTracks;

  // Sync externally-controlled selection (if provided) into the id-keyed Set.
  React.useEffect(() => {
    if (controlledSelectedIds) {
      setSelectedIds(new Set(controlledSelectedIds));
    }
  }, [controlledSelectedIds]);

  // Derive TanStack's RowSelectionState (keyed by track id via getRowId) from
  // the id Set. Excluded rows are filtered defensively in case one slips in.
  const rowSelection = useMemo<RowSelectionState>(() => {
    const rs: RowSelectionState = {};
    selectedIds.forEach((trackId) => {
      if (!excludeSet.has(trackId)) rs[trackId] = true;
    });
    return rs;
  }, [selectedIds, excludeSet]);

  // DataTable reports selection changes for the CURRENT page only. Reconcile
  // those into the global Set: keep ids from other pages, replace the ids that
  // belong to the rows currently visible.
  const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setSelectedIds((prev) => {
        const merged = new Set<string>();
        // Retain selections from pages other than the one currently visible.
        const visibleIds = new Set(tracks.map((tr) => tr.id));
        prev.forEach((id) => {
          if (!visibleIds.has(id)) merged.add(id);
        });
        // Apply the (id-keyed) selection state for the visible page.
        Object.keys(next).forEach((id) => {
          if (next[id] && !excludeSet.has(id)) merged.add(id);
        });
        return merged;
      });
    },
    [rowSelection, tracks, excludeSet],
  );

  const handleAddSelected = useCallback(() => {
    const ids = [...selectedIds].filter((id) => !excludeSet.has(id));
    onTracksSelected?.(ids);
    setSelectedIds(new Set());
  }, [selectedIds, excludeSet, onTracksSelected]);

  // Standalone bulk-edit: hand off the current selection to the parent (which
  // opens BulkEditTracksModal). Selection is intentionally retained so the user
  // sees the rows the edit will apply to; the parent clears it on success.
  const handleEditSelected = useCallback(() => {
    const ids = [...selectedIds].filter((id) => !excludeSet.has(id));
    onEditSelected?.(ids);
  }, [selectedIds, excludeSet, onEditSelected]);

  const handleManagePlaylists = useCallback(() => {
    const ids = [...selectedIds].filter((id) => !excludeSet.has(id));
    onManagePlaylists?.(ids);
  }, [selectedIds, excludeSet, onManagePlaylists]);

  const handleDeleteSelected = useCallback(() => {
    const ids = [...selectedIds].filter((id) => !excludeSet.has(id));
    onDeleteSelected?.(ids);
  }, [selectedIds, excludeSet, onDeleteSelected]);

  const selectedCount = selectedIds.size;

  // Standalone row-selection is opt-in via either bulk-action callback.
  const standaloneSelectable = mode === 'standalone' && Boolean(onEditSelected || onManagePlaylists || onDeleteSelected);

  // Select every selectable (non-already-added) track on the current page.
  const handleSelectAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const merged = new Set(prev);
      tracks.forEach((tr) => {
        if (!excludeSet.has(tr.id)) merged.add(tr.id);
      });
      return merged;
    });
  }, [tracks, excludeSet]);

  // Select every track matching the current filters (across all pages). Pulls
  // the full id set from the API in one large page, then drops already-added.
  const handleSelectAllMatching = useCallback(async () => {
    setIsSelectingAll(true);
    try {
      const all = await fetchLibraryTracks({
        page: 1,
        // Bound the one-shot fetch so a huge library can't pull an unbounded page into
        // memory; 500 also matches the bulk-update trackId cap (review P2).
        pageSize: Math.min(Math.max(totalCount, 1), 500),
        search: filters.search || undefined,
        genre: filters.genres.length > 0 ? filters.genres.join(',') : undefined,
        mood: filters.moods.length > 0 ? filters.moods.join(',') : undefined,
        status: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
        tags: filters.tags.length > 0 ? filters.tags.join(',') : undefined,
        sortBy: sort?.id || undefined,
        sortDesc: sort ? sort.desc : undefined,
      });
      setSelectedIds((prev) => {
        const merged = new Set(prev);
        all.items.forEach((tr) => {
          if (!excludeSet.has(tr.id)) merged.add(tr.id);
        });
        return merged;
      });
    } finally {
      setIsSelectingAll(false);
    }
  }, [totalCount, filters, sort, excludeSet]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(0);
  }, [filters.search, filters.genres, filters.moods, filters.statuses, filters.tags]);

  // Column definitions
  const columns = useMemo<ColumnDef<LibraryTrackDto, unknown>[]>(() => {
    const cols: ColumnDef<LibraryTrackDto, unknown>[] = [
      {
        id: 'thumbnail',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ row }) => {
          const track = row.original;
          const isCompleted = track.processingStatus === 'completed';
          const isPlayingInFloating = playingTrack?.id === track.id;
          return (
            <Box
              w="40px"
              h="40px"
              borderRadius="8px"
              overflow="hidden"
              bg="gray.100"
              flexShrink={0}
              position="relative"
              role="group"
              cursor={isCompleted && mode === 'standalone' ? 'pointer' : undefined}
              onClick={isCompleted && mode === 'standalone' ? (e) => {
                e.stopPropagation();
                handlePlayToggle(track);
              } : undefined}
              data-testid={isCompleted && mode === 'standalone' ? `library-play-button-${track.id}` : undefined}
            >
              {track.thumbnailUrl ? (
                <Image src={track.thumbnailUrl} alt="" w="100%" h="100%" objectFit="cover" />
              ) : (
                <Box w="100%" h="100%" bg={placeholderBg} />
              )}
              {isCompleted && mode === 'standalone' && (
                <Flex
                  position="absolute"
                  inset={0}
                  align="center"
                  justify="center"
                  bg="blackAlpha.500"
                  opacity={isPlayingInFloating ? 1 : 0}
                  _groupHover={{ opacity: 1 }}
                  transition="opacity 0.15s"
                  borderRadius="8px"
                >
                  <Icon viewBox="0 0 24 24" w={5} h={5} color="white">
                    {isPlayingInFloating
                      ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor" />
                      : <path d="M8 5v14l11-7L8 5z" fill="currentColor" />}
                  </Icon>
                </Flex>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'title',
        header: t('admin.library.trackTitle', 'Title'),
        cell: ({ row }) => {
          const track = row.original;
          const isAlreadyAdded = mode === 'selector' && excludeSet.has(track.id);
          return (
            <Flex align="center" gap={2} minW={0}>
              <Text
                color={isAlreadyAdded ? secondaryColor : textColor}
                fontWeight="600"
                fontSize="sm"
                noOfLines={1}
                cursor={onTrackClick && !isAlreadyAdded ? 'pointer' : undefined}
                _hover={onTrackClick && !isAlreadyAdded ? { color: 'brand.500' } : undefined}
                onClick={
                  onTrackClick && !isAlreadyAdded
                    ? (e: React.MouseEvent) => {
                        e.stopPropagation();
                        onTrackClick(track.id);
                      }
                    : undefined
                }
                data-testid={onTrackClick ? `track-link-${track.id}` : undefined}
              >
                {track.title}
              </Text>
              {isAlreadyAdded && (
                <Badge
                  colorScheme="gray"
                  fontSize="xs"
                  flexShrink={0}
                  data-testid={`already-added-badge-${track.id}`}
                >
                  {t('admin.playlists.alreadyAdded', 'Already added')}
                </Badge>
              )}
            </Flex>
          );
        },
      },
      {
        accessorKey: 'artist',
        header: t('admin.library.artist', 'Artist'),
        cell: ({ getValue }) => (
          <Text color={secondaryColor} fontSize="sm" noOfLines={1}>
            {(getValue() as string) || '-'}
          </Text>
        ),
      },
    ];

    // Show genre & mood columns only in standalone mode
    if (mode === 'standalone') {
      cols.push(
        {
          accessorKey: 'genre',
          header: t('admin.library.genre', 'Genre'),
          cell: ({ getValue }) => (
            <Text color={secondaryColor} fontSize="sm" noOfLines={1}>
              {(getValue() as string) ?? '-'}
            </Text>
          ),
        },
        {
          accessorKey: 'mood',
          header: t('admin.library.mood', 'Mood'),
          cell: ({ getValue }) => (
            <Text color={secondaryColor} fontSize="sm" noOfLines={1}>
              {(getValue() as string) ?? '-'}
            </Text>
          ),
        },
      );
    }

    cols.push(
      {
        id: 'status',
        header: t('admin.library.columns.status', 'Status'),
        accessorKey: 'processingStatus',
        cell: ({ getValue }) => (
          <TrackStatusBadge status={getValue() as ProcessingStatus} />
        ),
        size: 100,
      },
      {
        id: 'duration',
        header: t('admin.library.duration', 'Duration'),
        accessorKey: 'durationSeconds',
        cell: ({ getValue }) => (
          <Text color={secondaryColor} fontSize="sm">
            {formatDuration(getValue() as number)}
          </Text>
        ),
        size: 80,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: mode === 'standalone' ? 150 : 60,
        cell: ({ row }) => {
          const track = row.original;
          const canReprocess =
            track.processingStatus === 'completed' || track.processingStatus === 'failed';

          return (
            <Flex gap={1} onClick={(e) => e.stopPropagation()}>
              {onReprocess && canReprocess && (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => onReprocess(track.id)}
                  data-testid={`reprocess-button-${track.id}`}
                >
                  {t('admin.library.reprocess.button')}
                </Button>
              )}
            </Flex>
          );
        },
      },
    );

    return cols;
  }, [mode, t, textColor, secondaryColor, placeholderBg, onTrackClick, onReprocess, playingTrack?.id, handlePlayToggle, excludeSet]);

  const GENRE_OPTIONS = useMemo(() => [
    'Pop', 'Rock', 'Jazz', 'Classical', 'Electronic', 'Hip-Hop',
    'R&B', 'Country', 'Latin', 'Ambient', 'Lounge', 'Acoustic',
    'Indie', 'Blues', 'Folk', 'Funk', 'Soul', 'World',
  ], []);

  const MOOD_OPTIONS = useMemo(() => [
    'Energetic', 'Relaxing', 'Happy', 'Melancholic', 'Upbeat',
    'Chill', 'Romantic', 'Dramatic', 'Peaceful', 'Motivating',
    'Nostalgic', 'Dreamy', 'Groovy', 'Intense',
  ], []);

  const STATUS_OPTIONS = useMemo(() => ['pending', 'processing', 'completed', 'failed'], []);

  const toggleTag = useCallback((tag: string) => {
    setTags(filters.tags.includes(tag) ? filters.tags.filter(t => t !== tag) : [...filters.tags, tag]);
  }, [filters.tags, setTags]);

  const filterSections = useMemo<FilterSection[]>(
    () => {
      const sections: FilterSection[] = [
        {
          key: 'genre',
          label: t('admin.library.filterByGenre', 'Genre'),
          options: GENRE_OPTIONS.map((g) => ({ value: g, label: g })),
          selected: filters.genres,
          onToggle: toggleGenre,
        },
        {
          key: 'mood',
          label: t('admin.library.filterByMood', 'Mood'),
          options: MOOD_OPTIONS.map((m) => ({ value: m, label: m })),
          selected: filters.moods,
          onToggle: toggleMood,
        },
        {
          key: 'status',
          label: t('admin.library.filterByStatus', 'Status'),
          options: STATUS_OPTIONS.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
          selected: filters.statuses,
          onToggle: toggleStatus,
        },
      ];
      if (tagPool.length > 0) {
        // Tag filter section (FR-060) — sourced from FR-048's full library tag pool.
        // Multi-select: each click toggles a tag. Backend applies AND semantics.
        sections.push({
          key: 'tags',
          label: t('admin.library.filterByTags', 'Tags'),
          options: tagPool.map((tag) => ({ value: tag, label: tag })),
          selected: filters.tags,
          onToggle: toggleTag,
        });
      }
      return sections;
    },
    [t, filters.genres, filters.moods, filters.statuses, filters.tags, tagPool, toggleGenre, toggleMood, toggleStatus, toggleTag, GENRE_OPTIONS, MOOD_OPTIONS, STATUS_OPTIONS],
  );

  const sidebarFilterCount =
    filters.genres.length + filters.moods.length + filters.statuses.length + filters.tags.length;

  const handleClearAll = useCallback(() => {
    setGenres([]);
    setMoods([]);
    setStatuses([]);
    setTags([]);
  }, [setGenres, setMoods, setStatuses, setTags]);

  if (isError) {
    return (
      <Box p={4} textAlign="center">
        <Text color="red.500">{t('admin.common.noResults')}</Text>
      </Box>
    );
  }

  // Selector mode: compact layout with LibraryFilterBar
  if (mode === 'selector') {
    return (
      <Flex direction="column" flex={1} gap={2} data-testid="library-browser-panel">
        <LibraryFilterBar
          searchInput={searchInput}
          filters={filters}
          onSearchChange={setSearchInput}
          onGenreChange={(v) => setGenres(v ? [v] : [])}
          onMoodChange={(v) => setMoods(v ? [v] : [])}
          onStatusChange={(v) => setStatuses(v ? [v] : [])}
          compact
        />
        {tagPool.length > 0 && (
          <Box px={2} data-testid="library-tag-filter">
            <TagMultiSelect
              value={filters.tags}
              onChange={setTags}
              suggestions={tagPool}
              allowCreate={false}
              placeholder={t('admin.library.tagsFilterPlaceholder', 'Filter by tags...')}
              size="sm"
              wrapInFormControl={false}
              inputTestId="library-tag-filter-input"
            />
          </Box>
        )}
        <Flex justify="space-between" align="center" px={2} gap={2} flexWrap="wrap">
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text color={secondaryColor} fontSize="sm">
              {totalCount} {t('admin.playlists.tracks', 'tracks')}
            </Text>
            <Button
              size="xs"
              variant="link"
              colorScheme="brand"
              onClick={handleSelectAllOnPage}
              isDisabled={tracks.length === 0}
              data-testid="library-select-all-page-button"
            >
              {t('admin.playlists.selectAllOnPage', 'Select all on this page')}
            </Button>
            <Button
              size="xs"
              variant="link"
              colorScheme="brand"
              onClick={handleSelectAllMatching}
              isLoading={isSelectingAll}
              isDisabled={totalCount === 0}
              data-testid="library-select-all-matching-button"
            >
              {t('admin.playlists.selectAllMatching', 'Select all matching filters')}
            </Button>
            {selectedCount > 0 && (
              <Button
                size="xs"
                variant="link"
                colorScheme="gray"
                onClick={handleClearSelection}
                data-testid="library-clear-selection-button"
              >
                {t('admin.playlists.clearSelection', 'Clear')}
              </Button>
            )}
          </Flex>
          <Button
            size="md"
            colorScheme="brand"
            isDisabled={selectedCount === 0}
            onClick={handleAddSelected}
            data-testid="library-add-selected-button"
          >
            {t('admin.playlists.addSelected', 'Add Selected')} ({selectedCount})
          </Button>
        </Flex>
        <DataTable
          data={tracks}
          columns={columns}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          onSortChange={setSort}
          isLoading={isLoading}
          enableRowSelection={(row) => !excludeSet.has(row.id)}
          rowSelection={rowSelection}
          onRowSelectionChange={handleRowSelectionChange}
          getRowId={(row) => row.id}
          emptyText={t('admin.library.noTracksFound', 'No tracks found')}
          showCard={false}
          data-testid="library-track-list"
        />
      </Flex>
    );
  }

  // Standalone mode: collapsible FilterSidebar
  return (
    <Flex gap={4} data-testid="library-browser-panel">
      {isSidebarOpen && (
        <FilterSidebar
          sections={filterSections}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          onClose={() => setIsSidebarOpen(false)}
          onClearAll={handleClearAll}
        />
      )}

      <Flex direction="column" flex={1}>
        <DataTable
          headerContent={
            <Flex align="center" gap={3} mb={4}>
              {!isSidebarOpen && (
                <>
                  <Input
                    placeholder={t('admin.common.search', 'Search...')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    flex={1}
                    minW={200}
                    size="md"
                    borderRadius="14px"
                  />
                  <Button
                    leftIcon={<FilterIcon />}
                    variant="outline"
                    size="md"
                    borderRadius="10px"
                    onClick={() => setIsSidebarOpen(true)}
                  >
                    {t('admin.common.filters', 'Filters')}
                    {sidebarFilterCount > 0 && (
                      <Badge ml={2} colorScheme="brand" borderRadius="full" fontSize="xs">
                        {sidebarFilterCount}
                      </Badge>
                    )}
                  </Button>
                </>
              )}
              {standaloneSelectable && selectedCount > 0 && (
                <Flex
                  align="center"
                  gap={2}
                  ms={isSidebarOpen && !actionButton ? 'auto' : undefined}
                  data-testid="library-bulk-edit-toolbar"
                >
                  <Badge colorScheme="brand" borderRadius="full" px={2} py={0.5} data-testid="library-selection-count">
                    {t('admin.library.bulkEdit.selectedCount', { count: selectedCount })}
                  </Badge>
                  <Button
                    size="md"
                    variant="link"
                    colorScheme="gray"
                    onClick={handleClearSelection}
                    data-testid="library-clear-selection-button"
                  >
                    {t('admin.playlists.clearSelection', 'Clear')}
                  </Button>
                  {onEditSelected && (
                    <Button
                      size="md"
                      variant="outline"
                      onClick={handleEditSelected}
                      data-testid="library-edit-selected-button"
                    >
                      {t('admin.library.bulkEdit.editMetadata', 'Edit metadata')}
                    </Button>
                  )}
                  {onManagePlaylists && (
                    <Button
                      size="md"
                      colorScheme="brand"
                      onClick={handleManagePlaylists}
                      data-testid="library-manage-playlists-button"
                    >
                      {t('admin.playlists.manage.action', 'Manage playlists')}
                    </Button>
                  )}
                  {onDeleteSelected && (
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme="red"
                      onClick={handleDeleteSelected}
                      data-testid="library-delete-selected-button"
                    >
                      {t('admin.library.bulkDelete.action', 'Delete')}
                    </Button>
                  )}
                </Flex>
              )}
              {actionButton && <Box ms={isSidebarOpen ? 'auto' : undefined}>{actionButton}</Box>}
            </Flex>
          }
          data={tracks}
          columns={columns}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          onSortChange={setSort}
          isLoading={isLoading}
          onRowClick={onTrackClick ? (row) => onTrackClick(row.id) : undefined}
          enableRowSelection={standaloneSelectable}
          rowSelection={standaloneSelectable ? rowSelection : undefined}
          onRowSelectionChange={standaloneSelectable ? handleRowSelectionChange : undefined}
          getRowId={standaloneSelectable ? (row) => row.id : undefined}
          emptyText={t('admin.library.noTracksFound', 'No tracks found')}
          showCard
          data-testid="library-track-list"
        />
      </Flex>

      {/* Spacer so pagination isn't hidden behind the fixed floating player */}
      {playingTrack && <Box h="80px" />}

      {/* Floating audio player */}
      {playingTrack && playerStreamUrl && (
        <FloatingAudioPlayer
          track={playingTrack}
          streamUrl={playerStreamUrl}
          sasToken={playerSasToken}
          onClose={handleClosePlayer}
        />
      )}
    </Flex>
  );
}
